// src/composables/useInstallPrompt.js
// PWA install state for the mobile sidecar. A singleton listener (attached at
// import) captures Android's `beforeinstallprompt`; the composable exposes
// install/dismiss + platform/standalone detection so the banner can show an
// "Install" button (Android) or an "Add to Home Screen" hint (iOS Safari, which
// has no programmatic install prompt).

import { ref } from 'vue'

const deferredPrompt = ref(null)
const canInstall = ref(false)
const installed = ref(false)
const dismissed = ref(false)

const DISMISS_KEY = 'gs_pwa_install_dismissed'
try {
  dismissed.value = localStorage.getItem(DISMISS_KEY) === '1'
} catch {
  /* ignore */
}

function detectStandalone() {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    )
  } catch {
    return false
  }
}
const standalone = ref(detectStandalone())

const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || ''
// iOS Safari only (A2HS works there); exclude Chrome/Firefox/Edge-on-iOS.
const isIos = /iphone|ipad|ipod/i.test(ua) && !/(crios|fxios|edgios)/i.test(ua)
const isAndroid = /android/i.test(ua)

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', e => {
    e.preventDefault()
    deferredPrompt.value = e
    canInstall.value = true
  })
  window.addEventListener('appinstalled', () => {
    installed.value = true
    standalone.value = true
    canInstall.value = false
    deferredPrompt.value = null
  })
}

export function useInstallPrompt() {
  async function promptInstall() {
    const e = deferredPrompt.value
    if (!e) return
    try {
      e.prompt()
      await e.userChoice
    } catch {
      /* ignore */
    }
    deferredPrompt.value = null
    canInstall.value = false
  }
  function dismiss() {
    dismissed.value = true
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      /* ignore */
    }
  }
  return { canInstall, installed, standalone, dismissed, isIos, isAndroid, promptInstall, dismiss }
}
